import {
  AdminRegistration,
  AdminSelfExclusionData,
  AdminUser,
  CreateEmailTemplate,
  EmailResend,
  PartnershipUpdateData,
  PlayerUser,
  SpecialRefEditRequest,
  SpecialRefRequest,
  SubmitRoleType,
  WhitelistIpRequest,
} from "@/types";
import { backendApi, ggrApi } from "./api.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { TransactionTypeEnum } from "@/components/admin/transactionmodule/types.tsx";

export type TransactionsPaginationRequest = {
  transactionType?: TransactionTypeEnum;
  method?: string;
  startDate?: string;
  endDate?: string;
  username?: string;
  page?: number;
  pageSize?: number;
};

export type TransactionData = {
  id: string;
  txHash: string;
  transactionType: string;
  walletAddress: string;
  tokenName: string;
  tokenAddress: string;
  chain: string;
  walletId: string;
  userId: string;
  amount: number;
  usdAmount: number;
  rakeBackAmount: number;
  rakeBackPrecentage: number;
  currency: string;
  isClaimed: boolean;
  isWin: boolean;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  gameId: string | null;
  isNative: boolean;
  method: string;
  network: string | null;
  User: {
    userName: string;
  };
};
export type TransactionsPaginationResp = {
  data: TransactionData[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
};

export type PaginationRequest = {
  page?: number;
  pageSize?: number;
  name?: string;
  order?: string;
  sort?: string;
  filter?: string;
  searchKey?: string;
  searchValue?: string;
};

export const usePaginateTransactions = (
  pagination: TransactionsPaginationRequest,
) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["all-transactions"],
    queryFn: async () => {
      const response = await backendApi.get("/transactions", config);
      return response;
    },
    enabled: false,
  });
};

export const useGetAllRoles = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-roles"],
    queryFn: () => backendApi.get("/admin/role/get-all-roles", config),
    enabled: false,
    retry: false,
  });
};

export const useGetAllPrivileges = () => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-all-privileges"],
    queryFn: async () => {
      const response = await backendApi.get(
        "/admin/role/get-all-privileges",
        config,
      );
      return response.data;
    },
    retry: false,
  });
};

export const useGetAllUsers = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-users"],
    queryFn: () => backendApi.get("/admin/user/get-all-users", config),
    retry: false,
    enabled: false,
  });
};

export const useGetAllAdmins = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-users", pagination],
    queryFn: () => backendApi.get("/admin/user/get-all-users", config),
    retry: false,
  });
};

export const useGetWhitelistStatus = () => {
  const config = { withCredentials: true };
  return useQuery({
    queryKey: ["get-whitelist-status"],
    queryFn: () => backendApi.get("/whitelist", config),
  });
};

export const useGetWhitelistIPs = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-whitelist-ips", pagination],
    queryFn: () => backendApi.get("/admin/user/whitelist", config),
  });
};

export const useAddWhitelistIP = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: WhitelistIpRequest) =>
      backendApi.post("/admin/user/whitelist", data, config),
  });
};

export const useDeleteWhitelistIP = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: WhitelistIpRequest) =>
      backendApi.patch("/admin/user/delete-whitelist", data, config),
  });
};

export const useUpdateWhitelistIpStatus = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (status: boolean) =>
      backendApi.patch("/whitelist", { status }, config),
  });
};

export const useGetAllPlayers = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-players", pagination],
    queryFn: () => backendApi.get("/admin/player/get-all-players", config),
    retry: false,
    enabled: false,
  });
};

export const useGetRevenueByProvider = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-revenue-by-provider", pagination],
    queryFn: () => backendApi.get("/admin/dashboard/revenueByProvider", config),
    retry: false,
    enabled: false,
  });
};

export const useGetRevenueByCategory = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-revenue-by-category", pagination],
    queryFn: () => backendApi.get("/admin/dashboard/revenueByCategory", config),
    retry: false,
    enabled: false,
  });
};

export const useGetDashboardData = (timeline: string) => {
  return useQuery({
    queryKey: ["get-dashboard-data", timeline],
    queryFn: async () => {
      const response = await backendApi.get(
        `admin/dashboard/overview?timeline=${timeline}`,
        {
          withCredentials: true,
        },
      );
      return response.data;
    },
  });
};

export const useGetDashboardCsv = (timeline: string) => {
  return useQuery({
    queryKey: ["get-dashboard-csv", timeline],
    queryFn: async () => {
      const response = await backendApi.get(
        `admin/dashboard/csvData?timeline=${timeline}`,
        {
          withCredentials: true,
        },
      );
      return response.data;
    },
  });
};

export const useGetAllTiers = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-tiers", pagination],
    queryFn: () => backendApi.get("/admin/tier/get-all-tiers", config),
  });
};

export const useGetAllReferral = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };

  return useQuery({
    queryKey: ["get-all-referral"],
    queryFn: async () => {
      const response = await backendApi.get(
        "/admin/referral/get-all-referral",
        config,
      );
      return response;
    },
    enabled: false,
  });
};

export const useGetGenerateByOwner = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["generate-by-owner", pagination],
    queryFn: () => backendApi.get("/admin/referral/generate-by-owner", config),
  });
};

export const useCreateUser = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: AdminRegistration) =>
      backendApi.post("/admin/user/add", data, config),
  });
};

export const useCreateRole = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: AdminRegistration) =>
      backendApi.post("/admin/role/add", data, config),
  });
};

export const useUpdateUser = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AdminUser> }) =>
      backendApi.patch(`/admin/user/edit/${id}`, data, config),
  });
};

export const useUpdatePlayer = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PlayerUser> }) =>
      backendApi.patch(`/admin/player/edit/${id}`, data, config),
  });
};

export const useUpdateRole = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubmitRoleType }) =>
      backendApi.patch(`/admin/role/edit/${id}`, data, config),
  });
};

export const useGetSelfExclusionHistory = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-self-exclusion-history", pagination],
    queryFn: () =>
      backendApi.get("/admin/self-exclusion/histories/all", config),
    retry: 0,
  });
};

export const useGetEmailHistory = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-email-history", pagination],
    queryFn: () => backendApi.get("/admin/email/email-status", config),
  });
};

export const useGetEmailTemplateTypes = () => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-all-email-template-types"],
    queryFn: async () => {
      const response = await backendApi.get(
        "/admin/email/email-templates/types",
        config,
      );
      return response.data;
    },
  });
};

export const useCreateEmailTemplate = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: CreateEmailTemplate) =>
      backendApi.post("/admin/email/email-templates", data, config),
  });
};

export const useUpdateEmailTemplate = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateEmailTemplate }) =>
      backendApi.put(`/admin/email/email-templates/${id}`, data, config),
  });
};

export const useGetEmailTemplateDetails = (id: string) => {
  const config = { withCredentials: true };

  return useQuery({
    queryKey: ["get-email-template-details", id],
    queryFn: async () => {
      const response = await backendApi.get(
        `/admin/email/email-templates/${id}`,
        config,
      );
      return response.data;
    },
  });
};

export const useAdminRequestSelfExclusion = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: AdminSelfExclusionData) =>
      backendApi.post("/admin/self-exclusion", data, config),
  });
};

export const useResendEmail = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: EmailResend) =>
      backendApi.post("/admin/self-exclusion/re-send", data, config),
  });
};

export const useGetAllUserName = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-usernames", pagination],
    queryFn: () =>
      backendApi.get("/admin/special-referral/get-usernames", config),
  });
};

export const useAddBulkReferrals = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: SpecialRefRequest) =>
      backendApi.post("/admin/special-referral/add-referral", data, config),
  });
};

export const useGetSpecialReferrals = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-special-referrals", pagination],
    queryFn: () =>
      backendApi.get("/admin/special-referral/all-special-referrals", config),
  });
};

export const useEditBulkReferrals = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (data: SpecialRefEditRequest) =>
      backendApi.put(
        "/admin/special-referral/update-bulk-referrals",
        data,
        config,
      ),
  });
};

export const useEditReferral = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SpecialRefEditRequest }) =>
      backendApi.put(
        `/admin/special-referral/update-single-referral/${id}`,
        data,
        config,
      ),
  });
};

type PartnershipsPaginationRequest = {
  page?: number;
  pageSize?: number;
  name?: string;
};

export const useGetAllPartnerships = (
  pagination: PartnershipsPaginationRequest,
) => {
  const config = { withCredentials: true, params: pagination };

  return useQuery({
    queryKey: ["get-all-partnerships", pagination],
    queryFn: () => backendApi.get("/admin/partnerships/get-all", config),
    enabled: false,
    retry: false,
  });
};

export const useApprovePartnership = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (id: string) => {
      return backendApi.patch(
        `/admin/partnerships/approve/${id}`,
        null,
        config,
      );
    },
    onSuccess: () => {
      toast.success("Partnership approved successfully.");
    },
    onError: (error: any) => {
      toast.error("Error approving partnership: " + error.message);
    },
  });
};

export const useRejectPartnership = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: (id: string) => {
      return backendApi.patch(`/admin/partnerships/reject/${id}`, null, config);
    },
    onSuccess: () => {
      toast.success("Partnership rejected successfully.");
    },
    onError: (error: any) => {
      toast.error("Error rejecting partnership: " + error.message);
    },
  });
};

export const useUpdatePartnership = () => {
  const config = { withCredentials: true };
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PartnershipUpdateData }) =>
      backendApi.put(
        `/admin/partnerships/edit-partnership/${id}`,
        data,
        config,
      ),
  });
};

export const useGetRevenues = (startDate: string, endDate: string) => {
  const config = {
    params: {
      startDate,
      endDate,
    },
  };

  return useQuery({
    queryKey: ["get-revenues"],
    queryFn: async () => {
      const response = await ggrApi.get(`/report`, config);
      return response.data;
    },
  });
};

export const useGetAllBalances = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["get-all-balances", pagination],
    queryFn: () => backendApi.get("/admin/user/balances", config),
    retry: false,
    enabled: false,
  });
};

export const useGetActivityLogs = (pagination: PaginationRequest) => {
  const config = { withCredentials: true, params: pagination };
  return useQuery({
    queryKey: ["logs", pagination],
    queryFn: () => backendApi.get("/admin/logs", config),
    retry: false,
    enabled: false,
  });
};
